import { Suspense, useState } from "react";
import { Routes, Route, useLocation, Navigate } from "react-router-dom";
import Topbar from "./scenes/global/Topbar";
import Login from "./scenes/authenticate/Login";
import UserList from "./scenes/user-list";
import { CssBaseline, ThemeProvider, IconButton } from "@mui/material";
import { ColorModeContext, themeSettings, useMode } from "./theme";
import PrivateRoute from "./util/PrivateRouter";

import Drawer from "@mui/material/Drawer";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { styled, useTheme } from "@mui/material/styles";
import MuiAppBar from "@mui/material/AppBar";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import URLLookup from "./scenes/url-lookup";
import AnalysisReport from "./scenes/analysis-report";
import UserEdit from "./scenes/user-edit";
import UserAdd from "./scenes/user-add";
import ResetPassword from "./scenes/reset-password";
import ExploreIcon from "@mui/icons-material/Explore";
import { useContextAuthentication } from "./util/useContextAuth";
import { ToastContainer } from "react-toastify";

const drawerWidth = 240;

const Main = ({ open, hide, children }) => {
  const theme = useTheme();

  const padding =
    location.pathname === "/url-lookup" || (location.pathname === "/" && hide)
      ? 0
      : 0;

  return (
    <main
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center", // Center content horizontally
        justifyContent: "center", // Center content vertically
        flexGrow: 1,
        padding: padding,
        transition: theme.transitions.create("margin", {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.leavingScreen,
        }),
        marginLeft: `-${drawerWidth}px`,
        ...(open && {
          transition: theme.transitions.create("margin", {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen,
          }),
          marginLeft: 0,
        }),
      }}
    >
      {children}
    </main>
  );
};

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  transition: theme.transitions.create(["margin", "width"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["margin", "width"], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: `${drawerWidth}px`,
  }),
}));

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
  justifyContent: "flex-end",
}));

function App() {
  const contextAuth = useContextAuthentication();

  const valueContext = contextAuth?.value,
    isAdmin = !!valueContext?.isAdmin,
    keyUser = valueContext?.key;

  const [theme, colorMode] = useMode();
  const [isSidebar, setIsSidebar] = useState(true);

  const { pathname } = useLocation();
  const shouldShowTopbar = pathname !== "/login" && pathname !== "/login/" && pathname !== "/analysis-report";
  const shouldShowhHeader =
    pathname !== "/url-lookup" && pathname !== "/analysis-report" && !(location.pathname === "/" && !isAdmin);

  const shouldShowMenu = pathname !== "/login";

  const [open, setOpen] = useState(false);

  const handleDrawerOpen = () => {
    setOpen(!open);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const navigate = useNavigate();

  const handleItemClick = (route) => {
    navigate(route);
  };

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <ToastContainer />
      <AppBar position="fixed" open={open}>
        {shouldShowTopbar && (
          <Topbar
            setIsSidebar={setIsSidebar}
            className="topbar"
            handleDrawerOpen={handleDrawerOpen}
            isAdmin={isAdmin}
          />
        )}
      </AppBar>
      {shouldShowMenu && (
        <Drawer
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              boxSizing: "border-box",
              zIndex: (theme) => theme.zIndex.drawer - 1,
            },
          }}
          variant="persistent"
          anchor="left"
          open={open}
        >
          <Divider />
          <List>
            <ListItem key={4} disablePadding>
              <ListItemButton onClick={() => handleItemClick("/url-lookup")}>
                <ListItemIcon>
                  <ExploreIcon />
                </ListItemIcon>
                <ListItemText primary="URL Lookup" />
              </ListItemButton>
            </ListItem>
          </List>
          <Divider />
          <DrawerHeader style={{ position: "absolute", bottom: 0, left: 0 }}>
            <IconButton onClick={handleDrawerClose}>
              <KeyboardDoubleArrowLeftIcon />
            </IconButton>
            <span style={{ marginLeft: "8px" }}>close sidebar</span>
          </DrawerHeader>
        </Drawer>
      )}

      <Main open={open} hide={!isAdmin}>
        {shouldShowhHeader && <DrawerHeader />}
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/"
            key={keyUser}
            element={
              <PrivateRoute>
                <Navigate to="/url-lookup" replace />
              </PrivateRoute>
            }
          />          
          <Route
            path="/url-lookup"
            element={
              <PrivateRoute>
                <URLLookup />
              </PrivateRoute>
            }
          />
          <Route
            path="/user-list"
            element={
              <PrivateRoute>
                <UserList />
              </PrivateRoute>
            }
          />
          <Route
            path="/user-edit/:userId"
            element={
              <PrivateRoute>
                <UserEdit />
              </PrivateRoute>
            }
          />
          <Route
            path="/user-add/"
            element={
              <PrivateRoute>
                <UserAdd />
              </PrivateRoute>
            }
          />

          <Route
            path="/reset-password"
            element={
              <PrivateRoute>
                <ResetPassword />
              </PrivateRoute>
            }
          />
          <Route
            path="/analysis-report"
            element={
              <PrivateRoute>
                <AnalysisReport />
              </PrivateRoute>
            }
          />
        </Routes>
      </Main>
    </Box>
  );
}

export default App;
